import { request } from "@/Api"
import ApiAutocomplete from "@/common/ApiAutocomplete"
import FilesUploader, { FilesStore } from "@/common/FilesUploader"
import { Autocomplete, Grid, IconButton, InputLabel, TextField, Tooltip } from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { format, parse, set } from "date-fns"
import { useEffect, useState } from "react"
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import { currencies } from "@/constants"

export default function InvoiceForm({invoice, setInvoice, onFormDataTransfer, overrides, errors, setErrors}) {
    const [ isProcessingInvoice, setIsProcessingInvoice ] = useState()
    const [ invoiceFile, setInvoiceFile ] = useState(null)
    const [ invoiceFieldAdornments, setInvoiceFieldAdornments ] = useState({})

    const overriding = overrides.docket && overrides.vendor_email

    const handleInputChange = (field) => (event) => {
        let newVal = event.target.value
        const newInvoice = {...invoice}
        setErrors({...errors, [field]: null})
        if (field == "invoice_number") {
            setErrors({...errors, accounting_bill_number: null})
        }
        if (field == "docket") {
            newVal = newVal.toUpperCase()
        }
        setInvoice({...newInvoice, [field]: newVal})
    }
    const handleCalendarChange = (fieldName) => (newValue) => {
        let date = newValue
        if (date != null) {
            try {
                date = format(newValue, "y-MM-dd")
            } catch (e) {
                date = ""
            }
        }
        setInvoice({...invoice, [fieldName]: date})
        setErrors({...errors, [fieldName]: null})
    }
    const handleFileTransfer = (fileTransfer) => {
        const invoiceFile = fileTransfer[0]
        setIsProcessingInvoice(true)
        const formData = new FormData()
        formData.append("file", invoiceFile, invoiceFile.name)
        request.post("/accounting-bills/ocr", formData)
        .then(response=>{
            setInvoiceFile(invoiceFile)
            const invoiceData = response.data
            setInvoice({...invoiceData, docket: invoiceData.docket ? invoiceData.docket.toUpperCase() : ""})
            onFormDataTransfer(formData)
            checkProjectExist(invoiceData.docket)
            checkVendorExist(invoiceData.vendor_email)
        })
        .finally(()=>{
            setIsProcessingInvoice(false)
        })
    }
    const handleFileDelete = (file, id) => {
        setInvoice({})
        setInvoiceFile(null)
    }
    const handleAutocompleteChange = (field) => (event, val) => {
        setInvoice({...invoice, [field]: val})
        setErrors({...errors, [field]: null})
    }
    const checkProjectExist = (docket) => {
        request.get("check-project-docket-availability", {params: {
            docket: docket,
            schema: "organization_1"
        }})
        .then(response=>{
            const docketExist = response.data
            if (!docketExist) {
                setInvoiceFieldAdornments(invoiceFieldAdornments => ({
                    ...invoiceFieldAdornments,
                    docket: 
                    <Tooltip title="no projects found that are in production">
                        <HighlightOffRoundedIcon color="error"/>
                    </Tooltip>
                }))
            } else {
                setInvoiceFieldAdornments(invoiceFieldAdornments => ({
                    ...invoiceFieldAdornments,
                    docket: <CheckCircleOutlineRoundedIcon color="success"/>
                }))
            }
        })
    }
    const checkVendorExist = (vendorEmail) => {
        request.get("check-vendor-email-availability", {params: {
            vendor_email: vendorEmail,
            schema: "organization_1"
        }})
        .then(response=>{
            const vendorExist = response.data.available
            if (!vendorExist) {
                setInvoiceFieldAdornments(invoiceFieldAdornments => ({
                    ...invoiceFieldAdornments,
                    vendor_email: 
                    <Tooltip title={response.data.message}>
                        <HighlightOffRoundedIcon color="error"/>
                    </Tooltip>
                }))
            } else {
                setInvoiceFieldAdornments(invoiceFieldAdornments => ({
                    ...invoiceFieldAdornments,
                    vendor_email: <CheckCircleOutlineRoundedIcon color="success"/>
                }))
            }
        })
    }
    const handleInvoiceLineItemChange = (updatedLineItem, index) => {
        const updatedLineItems = [...invoice.items]
        updatedLineItems[index] = updatedLineItem
        setInvoice({...invoice, items: updatedLineItems})
    }
    const handleInvoiceLineItemDelete = (lineItem) => {
        const updatedLineItems = invoice.items.filter(item => item !== lineItem)
        setInvoice({...invoice, items: updatedLineItems})
    }
    const handleInvoiceLineItemAdd = () => {
        const newLineItem = {
            name: "",
            description: "",
            currency: "",
            amount: ""
        }
        setInvoice({...invoice, items: [...invoice.items, newLineItem]})
    }
    return (
        <>
            <div style={{paddingBottom: 10}}>
                <InputLabel>Please upload a PDF of your invoice below. A docket number, if applicable, must be included on your invoice. Please correct any fields that are scanned incorrectly.</InputLabel>
                <FilesStore
                    filesData={invoiceFile ? [invoiceFile] : []}
                    acceptedFormats={["application/pdf", "image/png", "image/jpeg"]}
                    onFilesTransfer={handleFileTransfer}
                    onFileDelete={handleFileDelete}
                    isFilesProcessing={isProcessingInvoice}
                    single
                />
            </div>

            <div hidden={!invoiceFile}>
                <Grid container spacing={2}>
                    <Grid item flexGrow={1}>
                        <InputLabel>Your Email</InputLabel>
                        <TextField
                            InputProps={{
                                endAdornment: invoiceFieldAdornments.vendor_email
                            }}
                            sx={{marginBottom: 2}}
                            fullWidth
                            size="small"
                            onBlur={(e)=>{checkVendorExist(e.target.value)}}
                            onChange={handleInputChange("vendor_email")}
                            value={invoice.vendor_email || ""}
                        />
                    </Grid>
                    <Grid item flexGrow={1}>
                        <InputLabel>Vendor Name</InputLabel>
                        <TextField
                            sx={{marginBottom: 2}}
                            fullWidth
                            size="small"
                            onChange={handleInputChange("vendor_name")}
                            value={invoice.vendor_name || ""}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item flexGrow={1}>
                        <InputLabel>Currency</InputLabel>
                        <Autocomplete
                            size="small"
                            value={invoice.currency || null}
                            options={currencies}
                            renderInput={(params) => <TextField 
                                {...params} sx={{marginBottom: 2}}
                                error={errors.currency != null}
                                helperText={errors.currency}
                            />}
                            onChange={handleAutocompleteChange("currency")}
                        />
                    </Grid>
                    <Grid item flexGrow={1}>
                        <InputLabel>Amount Before Tax</InputLabel>
                        <TextField
                            sx={{marginBottom: 2}}
                            fullWidth
                            size="small"
                            onChange={handleInputChange("total_without_tax")}
                            value={invoice.total_without_tax || ""}
                            error={errors.actual != null || errors.total_without_tax != null}
                            helperText={errors.actual || errors.total_without_tax}
                            type="number"
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item flexGrow={1}>
                        <InputLabel>Project Docket</InputLabel>
                        <TextField
                            InputProps={{
                                endAdornment: invoiceFieldAdornments.docket
                            }}
                            sx={{marginBottom: 2}}
                            fullWidth
                            size="small"
                            onChange={handleInputChange("docket")}
                            onBlur={(e)=>{checkProjectExist(e.target.value)}}
                            value={invoice.docket || ""}
                        />
                    </Grid>
                    <Grid item flexGrow={1}>
                        <InputLabel>Invoice Number</InputLabel>
                        <TextField
                            sx={{marginBottom: 2}}
                            fullWidth
                            size="small"
                            onChange={handleInputChange("invoice_number")}
                            value={invoice.invoice_number || ""}
                            error={errors.accounting_bill_number != null}
                            helperText={errors.accounting_bill_number}
                        />
                    </Grid>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid item flexGrow={1}>
                            <InputLabel>Invoice Date</InputLabel>
                            <DatePicker
                                value={invoice.invoice_date ? parse(invoice.invoice_date, "y-MM-dd", new Date()) : null}
                                inputFormat={"yyyy-MM-dd"}
                                onChange={handleCalendarChange("invoice_date")}
                                onAccept={handleCalendarChange("invoice_date")}
                                renderInput={(params) => (
                                    <TextField size="small" fullWidth {...params} 
                                    error={errors.invoice_date != null}
                                    helperText={errors.invoice_date}/>
                                )}
                            />
                        </Grid>
                        <Grid item flexGrow={1}>
                            <InputLabel>Due Date</InputLabel>
                            <DatePicker
                                value={invoice.due_date ? parse(invoice.due_date, "y-MM-dd", new Date()) : null}
                                inputFormat={"yyyy-MM-dd"}
                                onChange={handleCalendarChange("due_date")}
                                onAccept={handleCalendarChange("due_date")}
                                renderInput={(params) => (
                                    <TextField size="small" fullWidth {...params} />
                                )}
                            />
                        </Grid>
                    </LocalizationProvider>
                </Grid>
                <div hidden>
                    <h3 style={{paddingTop: 30}}>Line Items</h3>
                    <InvoiceLineItemHeader />
                    { invoice.items && invoice.items.map((lineItem, index) => (
                        <InvoiceLineItem
                            key={index}
                            index={index}
                            lineItemData={lineItem}
                            onChange={handleInvoiceLineItemChange}
                            onDelete={handleInvoiceLineItemDelete}
                        />
                    ))}
                    <div>
                        <IconButton size="small" onClick={handleInvoiceLineItemAdd}>
                            <AddCircleOutlineRoundedIcon />
                        </IconButton>
                    </div>
                </div>
            </div>
        </>
    )
}

const InvoiceLineItemHeader = () => {
    return (
        <Grid container spacing={2} sx={{marginBottom: 2}} alignItems={"center"} wrap="nowrap">
            <Grid item flexGrow={1} maxWidth={200}>
                <InputLabel>Name</InputLabel>
            </Grid>
            <Grid item flexGrow={1}>
                <InputLabel>Description</InputLabel>
            </Grid>
            <Grid item flexGrow={1} maxWidth={200}>
                <InputLabel>Currency</InputLabel>
            </Grid>
            <Grid item flexGrow={1} maxWidth={200}>
                <InputLabel>Amount</InputLabel>
            </Grid>
            <Grid item flexGrow={1} maxWidth={30}>
            </Grid>
        </Grid>
    )
}

const InvoiceLineItem = ({index, lineItemData, onChange, onDelete}) => {
    const [lineItem, setLineItem] = useState(lineItemData)
    useEffect(() => {
        setLineItem(lineItemData)
    }, [lineItemData])
    if (!lineItem) {
        return null
    }
    const handleInputChange = (field) => (event) => {
        const newVal = event.target.value
        const updatedLineItem = {...lineItem, [field]: newVal}
        setLineItem(updatedLineItem)
        onChange(updatedLineItem, index)
    }
    return (
        <Grid container spacing={2} sx={{marginBottom: 2}} alignItems={"center"} wrap="nowrap">
            <Grid item flexGrow={1} maxWidth={200}>
                <TextField
                    fullWidth
                    size="small"
                    value={lineItem.name || ""}
                    onChange={handleInputChange("name")}
                />
            </Grid>
            <Grid item flexGrow={1}>
                <TextField
                    fullWidth
                    size="small"
                    value={lineItem.description || ""}
                    onChange={handleInputChange("description")}
                />
            </Grid>
            <Grid item flexGrow={1} maxWidth={200}>
                <TextField
                    fullWidth
                    size="small"
                    value={lineItem.currency || ""}
                    onChange={handleInputChange("currency")}
                />
            </Grid>
            <Grid item flexGrow={1} maxWidth={200}>
                <TextField
                    fullWidth
                    size="small"
                    value={lineItem.amount || ""}
                    onChange={handleInputChange("amount")}
                />
            </Grid>
            <Grid item flexGrow={1} maxWidth={30}>
                <IconButton size="small" onClick={() => onDelete(lineItem)}>
                    <DeleteOutlineRoundedIcon />
                </IconButton>
            </Grid>
        </Grid>
    )
}